/* /assets/lazy-video.js
 *
 * Defers loading of background videos marked with [data-lazy-video].
 * The video element renders with only a poster and preload="none"; sources
 * stay off the element until it approaches the viewport.
 *
 * Rendition selection is viewport-aware: data-sources carries every mp4
 * rendition as JSON ({height, width, url}) and the smallest rendition that
 * is sharp for the element's rendered width (x devicePixelRatio, capped
 * at 2) is chosen. data-max-height (from the section's "Maximum video
 * quality" setting) caps the ceiling. data-src is a plain fallback used
 * when data-sources is absent or unparsable.
 *
 * Users with prefers-reduced-motion keep the poster only.
 */
(function () {
  'use strict';

  if (window.__lazyVideoInit) return;
  window.__lazyVideoInit = true;

  var SELECTOR = '[data-lazy-video]';
  var reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function displayWidth(video) {
    var width = video.getBoundingClientRect().width;
    if (!width && video.parentElement) {
      width = video.parentElement.getBoundingClientRect().width;
    }
    if (!width) {
      width = window.innerWidth;
    }
    // Cap DPR at 2: for video, bitrate matters more than pixel density,
    // and 3x phone screens gain nothing visible from a heavier rendition.
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    return width * dpr;
  }

  function pickSource(video) {
    var json = video.dataset.sources;
    if (!json) return video.dataset.src || null;

    var sources;
    try {
      sources = JSON.parse(json);
    } catch (e) {
      return video.dataset.src || null;
    }
    if (!sources || !sources.length) return video.dataset.src || null;

    var cap = parseInt(video.dataset.maxHeight, 10) || Infinity;
    var needed = displayWidth(video);

    sources.sort(function (a, b) {
      return a.height - b.height;
    });

    // Smallest rendition under the cap that covers the displayed width;
    // otherwise the largest under the cap; otherwise the smallest overall.
    var chosen = null;
    for (var i = 0; i < sources.length; i++) {
      if (sources[i].height > cap) continue;
      chosen = sources[i];
      if (sources[i].width >= needed) break;
    }
    if (!chosen) chosen = sources[0];
    return chosen.url;
  }

  function attach(video) {
    if (video.dataset.lazyVideoLoaded) return;
    var src = pickSource(video);
    if (!src) return;

    video.dataset.lazyVideoLoaded = 'true';
    // Programmatic play() is only permitted when muted is set on the element itself.
    video.muted = true;
    video.src = src;
    video.load();

    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      // Autoplay can be rejected (e.g. low-power mode on iOS); poster remains.
      playPromise.catch(function () {});
    }
  }

  function observe(video) {
    if (video.dataset.lazyVideoObserved) return;
    video.dataset.lazyVideoObserved = 'true';

    if (reducedMotion && reducedMotion.matches) return;

    if (!('IntersectionObserver' in window)) {
      attach(video);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            attach(entry.target);
          }
        });
      },
      // Start loading roughly one viewport before the video scrolls into view.
      { rootMargin: '100% 0px' }
    );

    observer.observe(video);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var videos = scope.querySelectorAll(SELECTOR);
    for (var i = 0; i < videos.length; i++) {
      observe(videos[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Theme editor: sections are re-rendered in place.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
